import type { DocMeta } from "@klarity/schema";
import type { ThemeTokens } from "@klarity/themes";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { usePrompt } from "../../components/ui/PromptDialog";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import { screenMeta } from "../canvas/ThemeCanvas";
import type { EditorHost } from "../editor/host";

/** The editor host for one document: attachments, uploads, @-search, navigation, prompts and theme labels. */
export function useDocumentHost(
  docId: string,
  tokens: ThemeTokens,
  meta: DocMeta,
): { host: EditorHost; dialogs: ReactNode } {
  const navigate = useNavigate();
  const { ask, element } = usePrompt();
  const { lang } = screenMeta(meta);
  const labels = tokens.labels[lang];
  const host = useMemo<EditorHost>(
    () => ({
      labels: { note: labels.note, warning: labels.warning },
      // Only attachment references become same-origin URLs; anything else must never reach <img>.
      attachmentUrl: (src) => (src.startsWith("att:") ? api.attachments.url(src) : ""),
      uploadImage: (file) => api.attachments.uploadImage(docId, file),
      searchDocs: async (q) =>
        (await api.docLinks(q)).map((d) => ({ code: d.code, title: d.title, spaceName: d.spaceName })),
      openDoc: (code) => void navigate({ to: "/d/$ref", params: { ref: code } }),
      notify: (message, tone) => toast(message, tone),
      askText: ask,
    }),
    [docId, labels.note, labels.warning, navigate, ask],
  );
  return { host, dialogs: element };
}
